import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAutomationTicket } from '../hooks/useAutomationTicket';

interface AutomationTicketDetailsProps {
  taskId?: string;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'done':
      return 'green';
    case 'pending':
      return 'yellow';
    default:
      return 'blue';
  }
};

const getStatusLabel = (status: string, t: (key: string) => string): string => {
  switch (status) {
    case 'done':
      return t('common.completed');
    case 'pending':
      return t('common.pending');
    default:
      return t('common.in_progress');
  }
};

const AutomationTicketDetails: React.FC<AutomationTicketDetailsProps> = ({
  taskId,
}) => {
  const { t } = useTranslation();
  const { automationTicket, loading, error } = useAutomationTicket(taskId);
  const [isExpanded, setIsExpanded] = React.useState(true);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (loading) {
    return (
      <Flex justifyContent="center" p={4}>
        <Spinner />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {t('common.error_loading_ticket')}
      </Alert>
    );
  }

  if (!automationTicket) {
    return null;
  }

  return (
    <Flex w="full" p="4">
      <Flex
        position="relative"
        bg="white"
        p="4"
        w="full"
        rounded="xl"
        flexDir="column"
        borderWidth="thin"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          cursor="pointer"
          onClick={toggleExpand}
        >
          <Heading as="h3" size="md" color="gray.700" translate="no">
            {t('common.ticket_details')}
          </Heading>
          <Button
            size="md"
            variant="ghost"
            onClick={toggleExpand}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} />
          </Button>
        </Flex>

        <Collapse in={isExpanded} animateOpacity>
          <Flex mb={4} alignItems="center">
            <Text fontWeight="bold" mr={2}>
              {t('common.code')}:
            </Text>
            <Text>
              {automationTicket.customCode || automationTicket.ticketCode}
            </Text>

            <Box ml="auto">
              <Badge colorScheme={getStatusColor(automationTicket.status)}>
                {getStatusLabel(automationTicket.status, t)}
              </Badge>
            </Box>
          </Flex>

          {automationTicket.automationTicketProperties.length > 0 && (
            <Table variant="simple" size="sm">
              <Tbody>
                {automationTicket.automationTicketProperties.map((property) => (
                  <Tr key={property.id}>
                    <Td fontWeight="bold" width="40%">
                      {property.answer?.question?.name || ''}:
                    </Td>
                    <Td>{property.answer?.formatContent || ''}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Collapse>
      </Flex>
    </Flex>
  );
};

export default AutomationTicketDetails;
